<template>
  <div>
    <b-row>
      <b-col class="text-left pb-4">
        <span class="h5 text-black pr-3">Today's stats</span>
        <span v-if="autoRefresh" class="text-muted font-weight-light">Auto-refreshes every 3 minutes</span>
        <b-btn @click="updateReport" size="sm" :disabled="loading" class="float-right">
          <span v-if="loading">Updating...</span>
          <span v-else>Refresh</span>
        </b-btn>
      </b-col>
    </b-row>
    <template v-if="loading || timeReport.length !== 0 || tableReport.length !== 0">
      <stats-bar :report="timeReport" :stats="statBarStats" :subStats="statsBarSubStats" :format-last-stat="true"/>
      <b-card no-body class="mt-3 mb-4">
        <template slot="header">
          <span class="h5">
            <span class="icon icon-Reports text-primary"/>
            Today's Statistics
          </span>
          <b-badge variant="success" v-if="admin">
            ${{ firestoreSpend }}
          </b-badge>
          <span class="float-right">
            <b-form-radio-group class="p-0" v-model="timezone">
              <b-form-radio class="py-0" v-for="option in timezoneOptions" :key="option.value"
                            :value="option.value"><small>{{option.text }}</small></b-form-radio>
            </b-form-radio-group>
          </span>
        </template>
        <div class="my-4">
          <report-time-graph
            :report="timeReport"
            :stats="graphStats"
            :fields="graphFields"
            :timezone="timezone"
            :second-axis-stats="['auctions']"
            interval="HOUR"
            :joint-tooltip="true">
          </report-time-graph>
        </div>
      </b-card>
      <report-table-card
        :loading="loading"
        :title="tableTitle"
        :report="tableReport"
        :stats="tableStats"
        :fields="tableFields">
      </report-table-card>
    </template>
    <no-results v-else></no-results>

  </div>
</template>

<script>
    import StatsBar from '@/components/StatsBar'
    import ReportTimeGraph from '@/reports/components/graphs/ReportTimeGraph'
    import ReportTableCard from '@/reports/components/ReportTableCard'
    import NoResults from '@/components/NoResults'

    import {ReportsApi} from '@/reports/api'
    import auth from '@/auth'

    import moment from 'moment'
    import 'moment-timezone'

    export default {
        mounted() {
            let timer
            if (this.autoRefresh) {
                timer = setInterval(() => {
                    this.updateReportIfVisible()
                }, 60000*30)
            }
            this.$nextTick().then(this.updateReport())
            this.timers.push(timer)
        },
        props: {
            tableTitle: {type: String, required: false, default: "Today's Report"},
            tableStats: {
                type: Array, required: false, default: () =>
                    ['bids', 'bid_rate', 'clears', 'clear_rate']
            },
            tableFields: {
                type: Array, required: false, default: () =>
                    ['pub_name']
            },
            graphStats: {
                type: Array, required: false, default: () =>
                    ['auctions', 'bids', 'bid_rate', 'clears', 'clear_rate', 'fill_rate']
            },
            statBarStats: {
                type: Array, required: false, default: () =>
                    ['auctions', 'bids', 'clears', 'fill_rate', 'clear_rate']
            },
            statsBarSubStats: {
                type: Array, required: false, default: () => [
                    {parent: 'bids', stat: 'bid_rate'}, {parent: 'clears', stat: 'clear_rate'}
                ]
            },
            autoRefresh: {type: Boolean, default: false}
        },
        created() {
            let cid = auth.details().company_id
            let mode = this.mode()
            let filterField = null
            switch (mode) {
                case 'demand':
                    filterField = 'bidder_id'
                    break
                case 'publisher':
                    filterField = 'pub_id'
                    break
                case 'exchange':
                    filterField = 'exchange_id'
            }
            if (filterField != null) {
                this.reportFilters.push({field: filterField, operator: 'EQUAL', value: cid})
            }

            this.admin = auth.hasPerm('ADMIN')
        },
        data() {
            return {
                admin: false,
                firestoreSpend: 0,
                reportFilters: [],
                graphFields: ['date'],
                timeReport: [],
                tableReport: [],
                timezone: 'UTC',
                timezoneOptions: [
                    {text: 'UTC', value: 'UTC'},
                    {text: 'Local Time (' + String(moment.tz.guess()) + ')', value: String(moment.tz.guess())}
                ],
                timers: [],
                loading: false,
            }
        },
        computed: {
            timeQuery() {
                return {
                    fields: [],
                    stats: this.graphStats,
                    filters: this.reportFilters,
                    startTime: moment().utc().startOf('day').valueOf(),
                    endTime: moment().utc().endOf('day').valueOf(),
                    interval: 'HOUR',
                    timezone: 'UTC',
                    dateAsMillis: true
                }
            },
            tableQuery() {
                return {
                    fields: this.tableFields,
                    stats: this.tableStats,
                    filters: this.reportFilters,
                    startTime: moment().utc().startOf('day').valueOf(),
                    endTime: moment().utc().endOf('day').valueOf(),
                    timezone: 'UTC',
                    dateAsMillis: true
                }
            },
        },
        methods: {
            mode() {
                return this.$route.path.split("/")[1]
            },
            updateReportIfVisible() {
                if (document.hasFocus()) {
                    this.updateReport()
                }
            },
            updateReport() {
                this.loading = true
                Promise.all([this.updateTimeReport(), this.updateTableReport(), this.updateFsSpend()]).then((data) => {
                    this.loading = false
                })
            },
            updateFsSpend() {
              return new Promise((resolve, reject) => {
                  let bid = null
                  let sid = null

                  let cid = auth.details().company_id
                  if(this.mode() === 'demand') {
                      bid = cid
                  } else if(this.mode() === 'publisher' || this.mode() === 'exchange') {
                      sid = cid
                  }

                  ReportsApi.fsreport(bid, sid, moment().utc().startOf('day').valueOf())
                      .then((spend) => {
                          this.firestoreSpend = spend
                          resolve()
                      }).catch(reject)
              })
            },
            updateTableReport() {
                return new Promise((resolve, reject) => {
                    ReportsApi.bqreport(this.tableQuery, this).then((data) => {
                        this.tableReport = data.results
                        resolve(data)
                    }, (e) => {
                        console.error(e)
                        reject(e)
                    })
                })
            },
            updateTimeReport() {
                return new Promise((resolve, reject) => {
                    ReportsApi.bqreport(this.timeQuery, this).then((data) => {
                        this.timeReport = data.results
                        resolve(data)
                    }, (e) => {
                        console.error(e)
                        reject(e)
                    })
                })
            }
        },
        beforeDestroy() {
            for (let timer of this.timers) {
                clearInterval(timer)
            }
        },
        components: {
            StatsBar,
            ReportTimeGraph,
            ReportTableCard,
            NoResults
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/UserDashboard.vue